import React, { useEffect, useState, useMemo } from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQGrid from '../../components/grid';
import UQSpacer from '../../components/spacer';
import UQInput from '../../components/input';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import UQCode from '../../components/code';
import { BREADCRUMB_ROOT } from '../../common/config';
import UQIcon from '../../components/icon';
import './style.scss';
import { UQCard } from '../..';

const ICONS_PER_PAGE = 50; // Show 50 icons at a time for better performance
const LOAD_MORE_INCREMENT = 25; // Load 25 more icons when "Load More" is clicked

const IconsPage = () => {
  const [icons, setIcons] = useState<string[]>([]);
  const [filteredIcons, setFilteredIcons] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [copiedIcon, setCopiedIcon] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [displayCount, setDisplayCount] = useState<number>(ICONS_PER_PAGE);

  useEffect(() => {
    const fetchIcons = async () => {
      setIsLoading(true);
      try {
        const icons = (import.meta as any).glob('/src/assets/icons/*.svg');
        const iconNames = Object.keys(icons)
          .map((filePath) => {
            const match = filePath.match(/\/([\w-]+)\.svg$/);
            return match ? match[1] : '';
          })
          .filter((name) => name !== '')
          .sort(); // Sort alphabetically for consistent ordering
        setIcons(iconNames);
        setFilteredIcons(iconNames);
      } catch (error) {
        console.error('Error fetching icons:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchIcons();
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
    setDisplayCount(ICONS_PER_PAGE); // Reset display count

    const filtered = icons.filter((icon) => icon.toLowerCase().includes(query));
    setFilteredIcons(filtered);
  };

  const handleCopy = (iconName: string) => {
    navigator.clipboard.writeText(iconName).then(() => {
      setCopiedIcon(iconName);
      setTimeout(() => setCopiedIcon(null), 2000);
    });
  };

  const totalPages = Math.ceil(filteredIcons.length / ICONS_PER_PAGE);
  const startIndex = (currentPage - 1) * ICONS_PER_PAGE;
  const endIndex = startIndex + ICONS_PER_PAGE;
  const currentIcons = useMemo(() => 
    filteredIcons.slice(startIndex, endIndex), 
    [filteredIcons, startIndex, endIndex]
  );

  const visibleIcons = useMemo(() => 
    filteredIcons.slice(0, displayCount), 
    [filteredIcons, displayCount]
  );

  const hasMoreIcons = displayCount < filteredIcons.length;

  const handleLoadMore = () => {
    setDisplayCount(prev => Math.min(prev + LOAD_MORE_INCREMENT, filteredIcons.length));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    document.querySelector('.icons-grid')?.scrollIntoView({ behavior: 'smooth' });
  };

  const attributes = [
    {
      attribute: 'icon',
      type: 'string',
      default: 'undefined',
      description: 'The name of the icon to display.',
    },
    {
      attribute: 'className',
      type: 'string',
      default: "''",
      description: 'Additional class names for custom styling.',
    },
    {
      attribute: 'size',
      type: 'string',
      default: "'1rem'",
      description: 'The size of the icon.',
    },
    {
      attribute: 'color',
      type: 'string',
      default: 'undefined',
      description: 'The color of the icon.',
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  const codeExample = `
import React from 'react';
import { UQIcon } from '@uniqconsulting/ui';

<UQIcon icon="check-circle"/>
`;

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Icons']} />
      <h1>UQIcon Demo</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQIcon</code> component is used to display SVG icons. It supports customizable sizes, colors, and additional class names for styling.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
      />
      <UQSpacer size="m" />

      {/* Code Example */}
      <h2>Code Example</h2>
      <UQSpacer size="s" />
      <UQCode code={codeExample} enableCopy />
      <UQSpacer size="m" />
      <UQCard header='Output'>
        <UQIcon icon="check-circle" />
      </UQCard>

      {/* Search Input */}
      <h2>Search Icons</h2>
      <UQInput
        label="Search Icons"
        name="search-icons"
        placeholder="Type to search icons..."
        value={searchQuery}
        onChange={handleSearch}
        clearable
        />
        <p>
          {isLoading ? (
            'Loading icons...'
          ) : (
            <>
              Showing <b>{visibleIcons.length}</b> of <b>{filteredIcons.length}</b> icon{filteredIcons.length !== 1 ? 's' : ''}
              {filteredIcons.length !== icons.length && (
                <> (filtered from {icons.length} total)</>
              )}
            </>
          )}
        </p>
      <UQSpacer size="m" />

      {/* Icons Grid */}
      <h2>Icons Grid</h2>
      <UQSpacer size="s" />
      
      {isLoading ? (
        <div className="loading-state">
          <UQIcon icon="loader" />
          <p>Loading icons...</p>
        </div>
      ) : (
        <>
          <UQGrid gap="1rem" className="icons-grid">
            {visibleIcons.map((icon) => (
              <div className="icon-container" key={icon}>
                <UQIcon icon={icon} />
                <p className="icon-name">{icon}</p>
                <button
                  className="copy-button"
                  onClick={() => handleCopy(icon)}
                  title="Copy icon name"
                >
                  {copiedIcon === icon ? 'Copied!' : 'Copy'}
                </button>
              </div>
            ))}
          </UQGrid>
          
          {/* Load More Button */}
          {hasMoreIcons && (
            <>
              <UQSpacer size="m" />
              <div className="load-more-container">
                <button 
                  onClick={handleLoadMore}
                  className="load-more-button"
                >
                  Load {Math.min(LOAD_MORE_INCREMENT, filteredIcons.length - displayCount)} More Icons
                </button>
                <p className="load-more-info">
                  {filteredIcons.length - displayCount} icons remaining
                </p>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default IconsPage;